import { UnitsTracker } from './formulas/units.ts';
import type { HazWasteUnit } from '../types/db.ts';

export const HAZ_WASTE_MASS_UNITS: readonly HazWasteUnit[] = [
  'lbs',
  'grams',
  'milligrams',
  'kg',
  'grains',
  'ounces',
  'tons',
  'cwt',
];

export const HAZ_WASTE_VOLUME_UNITS: readonly HazWasteUnit[] = ['gallons', 'liters', 'cubicfeet', 'fluidounces'];

export const HAZ_WASTE_UNIT_OPTIONS: readonly { value: HazWasteUnit; label: string }[] = [
  { value: 'lbs', label: 'Pounds (lb)' },
  { value: 'grams', label: 'Grams (g)' },
  { value: 'milligrams', label: 'Milligrams (mg)' },
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'grains', label: 'Grains (gr)' },
  { value: 'ounces', label: 'Ounces (oz)' },
  { value: 'tons', label: 'Short Tons (ton)' },
  { value: 'cwt', label: 'Hundredweight (cwt)' },
  { value: 'gallons', label: 'Gallons (gal)' },
  { value: 'liters', label: 'Liters (L)' },
  { value: 'cubicfeet', label: 'Cubic Feet (ft3)' },
  { value: 'fluidounces', label: 'Fluid Ounces (fl oz)' },
];

const KG_TO_LBS = 2.2046226218;
const LBS_PER_GALLON_OF_WATER = 8.3454045;

function convert(value: number, from: string, to: string): number | null {
  const converted = UnitsTracker.fromParsedUnitString(from).convertTo(UnitsTracker.fromParsedUnitString(to), value);
  return typeof converted === 'number' ? converted : null;
}

export function isHazWasteVolumeUnit(unit: HazWasteUnit): boolean {
  return HAZ_WASTE_VOLUME_UNITS.includes(unit);
}

export function hazWasteUnitLabel(unit: HazWasteUnit): string {
  return HAZ_WASTE_UNIT_OPTIONS.find((option) => option.value === unit)?.label ?? unit;
}

export function hazWasteUnitToKg(
  value: number,
  unit: HazWasteUnit,
  density: number | null | undefined,
  densityUnitOfMeasure: '1' | '2' | null | undefined,
): number | null {
  if (!isHazWasteVolumeUnit(unit)) return convert(value, unit, 'kg');
  if (!density || density <= 0 || !densityUnitOfMeasure) return null;

  const gallons = convert(value, unit, 'gallons');
  if (gallons === null) return null;
  const pounds = densityUnitOfMeasure === '2' ? gallons * density * LBS_PER_GALLON_OF_WATER : gallons * density;
  return pounds / KG_TO_LBS;
}

export function kgToHazWasteUnit(
  quantityKg: number,
  unit: HazWasteUnit,
  density: number | null | undefined,
  densityUnitOfMeasure: '1' | '2' | null | undefined,
): number | null {
  if (!isHazWasteVolumeUnit(unit)) return convert(quantityKg, 'kg', unit);
  if (!density || density <= 0 || !densityUnitOfMeasure) return null;

  const pounds = quantityKg * KG_TO_LBS;
  const gallons = densityUnitOfMeasure === '2' ? pounds / (density * LBS_PER_GALLON_OF_WATER) : pounds / density;
  return convert(gallons, 'gallons', unit);
}

/** EPA GM1 UOM codes. Units without their own GM1 code are normalized by dimension. */
export function hazWasteBiennialUnitCode(unit: HazWasteUnit): string {
  if (unit === 'tons') return '2';
  if (unit === 'kg' || unit === 'grams' || unit === 'milligrams') return '3';
  if (isHazWasteVolumeUnit(unit)) return '5';
  return '1';
}

/** e-Manifest UOM codes. Units without a direct code are normalized by dimension. */
export function hazWasteManifestUnitCode(unit: HazWasteUnit): string {
  if (unit === 'tons') return 'T';
  if (unit === 'kg' || unit === 'grams' || unit === 'milligrams') return 'K';
  if (unit === 'liters') return 'L';
  if (isHazWasteVolumeUnit(unit)) return 'G';
  return 'P';
}
